import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import levels from 'src/utils/logger/levels';
import consoleTransport from 'src/utils/logger/console';


export type Logger = {
  transports: Array<Transport>;
  levels: {
    [key: string]: number;
  };
  context: Record<any, any> | null | undefined;
  getContext(): Record<any, any>;
  log(level: string, message: string, data?: unknown): void;
  logInfo(arg1: LoggerInfo): void;
};

export type Transport = (info: LoggerInfo, formattedString: string) => void;

export type LoggerInfo = {
  timestamp?: string;
  level: string;
  message?: string | null | undefined;
  data?: unknown | null | undefined;
};

export type SenseLogger = typeof defaultLogger;

const baseLogger: Logger = {
  transports: [],
  levels: Object.freeze({}),
  context: undefined,

  // @ts-expect-error - TS2322 - Type '() => Record<any, any> | null | undefined' is not assignable to type '() => Record<any, any>'.
  getContext() {
    return this.context;
  },

  log(level: string, message: string, data: unknown) {
    const info = {
      level,
      message,
      data,
    } as const;

    this.logInfo(info);
  },

  logInfo({timestamp, level, message, ...rest}: LoggerInfo) {
    const info = {
      timestamp: timestamp ?? new Date().toISOString(),
      level,
      message,
      ...this.getContext(),
      ...rest,
    } as const;

    for (const transport of this.transports) {
      transport(info, JSON.stringify(info));
    }
  },
};

function createLogger<
  L extends {
    [key: string]: number;
  },
>({levels, transports}: {levels: L; transports: Transport[]}): Logger {
  return {
    ...baseLogger,
    levels,
    transports,
  };
}

function getLogAlias(level: string) {
  return function (message: string, data?: unknown) {
    // @ts-expect-error - TS2683 - 'this' implicitly has type 'any' because it does not have a type annotation.
    this._logger.log(level, message, data);
  };
}

function createLoggerWrapper<L extends Record<any, any>>(
  levels: L,
): Flow_ObjMap<L, () => (arg1: string, m?: unknown) => void> {
  const wrapper: Record<string, any> = {};
  for (const level in levels) {
    wrapper[level] = getLogAlias(level);
  }
  // @ts-expect-error - TS2322 - Type 'Record<string, any>' is not assignable to type 'ObjMap<L, () => (arg1: string, m?: unknown) => void>'.
  return wrapper;
}

export function createChildLogger(
  logger: DefaultLogger,
  context: Record<any, any>,
): DefaultLogger {
  return {
    ...logger,
    _logger: {
      ...logger._logger,
      context,
      getContext() {
        return {
          ...logger._logger.getContext(),
          ...this.context,
        };
      },
    },
  };
}

export function updateContext(logger: typeof defaultLogger, context: unknown) {
  logger._logger.context = {
    ...logger._logger.context,
    // @ts-expect-error - TS2698 - Spread types may only be created from object types.
    ...context,
  };
}

const transports = [consoleTransport];
if (
  process.env.EX_ENV === 'server' &&
  process.env.DEPLOY_ENV === 'production'
) {
  transports.push(require('src/utils/logger/syslog').default);
}

type LevelLog = (arg1: string, arg2?: unknown) => void;
export type DefaultLogger = {
  _logger: Logger;
  critical: LevelLog;
  error: LevelLog;
  warning: LevelLog;
  info: LevelLog;
  debug: LevelLog;
  log: LevelLog;
  warn: LevelLog;
  updateContext: (arg1: Record<any, any>) => void;
  createChild: (arg1: Record<any, any>) => DefaultLogger;
};
const defaultLogger: DefaultLogger = {
  _logger: createLogger({levels, transports}),
  ...createLoggerWrapper(levels),
  log: getLogAlias('info'),
  warn: getLogAlias('warning'),
  updateContext(context: Record<any, any>) {
    updateContext(this, context);
  },
  createChild(context: Record<any, any>) {
    return createChildLogger(this, context);
  },
};

export default defaultLogger;
